import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { SelectOption } from '../../../purchase-order-detail/sections/section-base.types';
import {
  FH_COMPANY_LABEL,
  FH_FIELD,
  FH_FIELD_ERROR,
  FH_REBATE,
} from '../../company-form.constants';
import { buildStaticValidators } from '../../company-form.validators';
import { CompanyFieldDescriptor, CompanyFormSection, FhRebateRow } from '../../company-form.types';

// Repeatable Agency Rebates rows.
//
// Figma only carries a placeholder for this card, so the row layout is designed to match the other
// section cards. Row validators are derived from the section's own field descriptors, so the
// min/max/decimals declared in companies-form.config.json stay the single source of truth.
@Component({
  selector: 'app-agency-rebates-card',
  templateUrl: './agency-rebates-card.component.html',
  styleUrls: ['./agency-rebates-card.component.scss'],
})
export class AgencyRebatesCardComponent {
  /* istanbul ignore next -- decorator metadata */
  @Input() section: CompanyFormSection;
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;

  readonly LABELS = FH_COMPANY_LABEL;
  readonly REBATE = FH_REBATE;
  readonly FIELD = FH_FIELD;

  constructor(private readonly fb: FormBuilder) {}

  get rows(): FormArray {
    return this.form.get(FH_FIELD.AGENCY_REBATES) as FormArray;
  }

  get rowGroups(): FormGroup[] {
    return (this.rows?.controls ?? []) as FormGroup[];
  }

  get currencyOptions(): SelectOption[] {
    return this.descriptor(FH_FIELD.REBATE_CURRENCY)?.options ?? [];
  }

  labelFor(fieldId: string): string {
    return this.descriptor(fieldId)?.label ?? fieldId;
  }

  addRow(): void {
    this.rows.push(AgencyRebatesCardComponent.createRow(this.fb, this.section?.fields ?? []));
    this.rows.updateValueAndValidity();
  }

  removeRow(index: number): void {
    this.rows.removeAt(index);
    this.rows.updateValueAndValidity();
  }

  removeAll(): void {
    while (this.rows.length) {
      this.rows.removeAt(0);
    }
    this.rows.updateValueAndValidity();
  }

  // Sorting happens on blur rather than on every keystroke — re-ordering mid-typing would move the
  // focused input out from under the caret.
  onThresholdBlur(): void {
    const sorted = AgencyRebatesCardComponent.sortRowValues(this.rows.getRawValue() as FhRebateRow[]);
    sorted.forEach((row, index) => {
      this.rowGroups[index]?.patchValue(row, { emitEvent: false });
    });
    this.rows.updateValueAndValidity();
  }

  isDuplicate(row: FormGroup): boolean {
    return !!row.get(FH_FIELD.REBATE_THRESHOLD)?.errors?.[FH_FIELD_ERROR.DUPLICATE_THRESHOLD];
  }

  showRowError(row: FormGroup, fieldId: string): boolean {
    const control = row.get(fieldId);
    return !!control && !!control.errors && (this.submitted || control.touched);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private descriptor(fieldId: string): CompanyFieldDescriptor | undefined {
    return (this.section?.fields ?? []).find(field => field.fieldId === fieldId);
  }

  // Row factory, also used by the page when rehydrating saved rows in edit mode.
  static createRow(
    fb: FormBuilder,
    descriptors: CompanyFieldDescriptor[],
    seed?: Partial<FhRebateRow>,
  ): FormGroup {
    const byId = new Map(descriptors.map(field => [field.fieldId, field] as [string, CompanyFieldDescriptor]));
    const validatorsFor = (fieldId: string): ValidatorFn[] => {
      const descriptor = byId.get(fieldId);
      const extra = descriptor ? buildStaticValidators(descriptor) : [];
      return [Validators.required, ...extra];
    };

    const currencyDefault = byId.get(FH_FIELD.REBATE_CURRENCY)?.defaultValue ?? FH_REBATE.DEFAULT_CURRENCY;

    return fb.group({
      [FH_FIELD.REBATE_CURRENCY]: [
        seed?.rebate_currency ?? currencyDefault,
        validatorsFor(FH_FIELD.REBATE_CURRENCY),
      ],
      [FH_FIELD.REBATE_THRESHOLD]: [
        seed?.rebate_threshold ?? null,
        validatorsFor(FH_FIELD.REBATE_THRESHOLD),
      ],
      [FH_FIELD.REBATE_PERCENTAGE]: [
        seed?.rebate_percentage ?? null,
        validatorsFor(FH_FIELD.REBATE_PERCENTAGE),
      ],
    });
  }

  // Ascending by threshold; rows without a threshold sort last so a half-typed row stays put.
  static sortRowValues(rows: FhRebateRow[]): FhRebateRow[] {
    return [...rows].sort((a, b) => {
      const left = a?.rebate_threshold;
      const right = b?.rebate_threshold;
      if (left === null || left === undefined) {
        return 1;
      }
      if (right === null || right === undefined) {
        return -1;
      }
      return Number(left) - Number(right);
    });
  }
}
